import React, { FC, useState } from 'react';

import { CHECKLIST__MAX_TOTAL_ITEMS } from '../../../constants/forms';
import { ChecklistDataDraft, Text } from '../../../models/checklist';

import ChecklistCreateInput from './ChecklistCreateInput';
import ChecklistHeader from './ChecklistHeader';
import ChecklistItems from './ChecklistItems';

import * as Styled from './styled';

type Props = {
  items: ChecklistDataDraft[];
  requestCreate: (text: Text) => void;
  requestDelete: (position: number) => void;
  requestDeleteAll: () => void;
  requestReorder: (items: ChecklistDataDraft[]) => void;
};

/**
 * Checklist component for the compose form
 *
 * @param props                  Props passed to the component
 * @param props.items            Checklist items added to the draft
 * @param props.requestCreate    Request that a new item is added
 * @param props.requestDelete    Request that an existing item is removed
 * @param props.requestDeleteAll Request that all items are removed
 * @param props.requestReorder   Request that the list is reordered
 *
 * @returns                      Component itself
 */
const ComposeChecklist: FC<Props> = ({
  items,
  requestCreate,
  requestDelete,
  requestDeleteAll,
  requestReorder,
}) => {
  const [isEditingMode, setIsEditingMode] = useState(false);

  const itemsCount = items.length;

  const canCreateMore = itemsCount < CHECKLIST__MAX_TOTAL_ITEMS;
  const shouldShowList = itemsCount > 0;

  /**
   * Handles request to delete all checklist items
   */
  const onDeleteAll = () => {
    requestDeleteAll();
    setIsEditingMode(false);
  };

  /**
   * Toggle editing mode switch
   */
  const toggleEditingMode = () => {
    setIsEditingMode(prev => !prev);
  };

  return (
    <Styled.Wrapper>
      {shouldShowList && (
        <Styled.Content>
          <ChecklistHeader
            isEditingMode={isEditingMode}
            requestDeleteAll={onDeleteAll}
            toggleEditingMode={toggleEditingMode}
          />
          <article>
            <ChecklistItems
              isEditingMode={isEditingMode}
              items={items}
              requestDelete={requestDelete}
              requestReorder={requestReorder}
            />
          </article>
        </Styled.Content>
      )}
      {canCreateMore && (
        <footer>
          <ChecklistCreateInput requestCreate={requestCreate} />
        </footer>
      )}
    </Styled.Wrapper>
  );
};

export default ComposeChecklist;
